import React, { useState, useRef } from "react";
import { Dialog } from 'primereact/dialog';
import { Calendar } from 'primereact/calendar';
import StoresDropDown from "./StoresDropDown.tsx";
import axios from "axios";
import { toast } from "../../components/Toast/toast.ts";
import moment from "moment";
import { ProgressSpinner } from 'primereact/progressspinner';
import { useClientQueryContext } from '../../context/ClientQueryContext.tsx';

const IncidentVideoModal = ({ visible, setVisible }) => {

  const { reloadClientQueries, setReloadClientQueries } = useClientQueryContext();
  const storedToken = localStorage.getItem("token");
  const [storename, setStoreName] = useState("");
  const [selectedStore, setSelectedStore] = useState([]);
  const [showChildModal, setShowChildModal] = useState(false);
  const [date, setDate] = useState(null);
  const [subject, setSubject] = useState("");
  const [time, setTime] = useState("");
  const [details, setDetails] = useState("");
  const [loading, setLoading] = useState(false);

  const formatDate = (date) => moment(date).format("YYYY-MM-DD");

  const resetInputs = () => {
    setStoreName("");
    setSelectedStore([]);
    setDate(null);
    setSubject("");
    setTime("");
    setDetails("");
  };

  const validateFormData = () => {
    if (!date || !details || !subject || !time || !selectedStore?.length || !storename) {
      toast.error("Please fill in all required fields.");
      return false;
    }
    return true;
  };

  const handleChildModalOpening = () => {
    if (validateFormData()) setShowChildModal(true);
  };

  const submitEvidenceDetails = async () => {
    try {
      if (!storedToken || !validateFormData()) return;
      setLoading(true);
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/cust-portal/evidence-request`,
        {
          evidence_date: formatDate(date),
          evidence_details: details,
          evidence_subject: subject,
          evidence_time: time,
          store_id: selectedStore,
          store_name: storename
        },
        { headers: { Authorization: `Bearer ${storedToken}` } }
      );
      if (response?.status === 200) {
        toast.success("Your Query has been submitted successfully!");
        setVisible(false);
        resetInputs();
        setReloadClientQueries(!reloadClientQueries);
      }
      setLoading(false);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "An error occurred. Please try again.");
      setLoading(false);
    }
  };

  const inputClass = "w-full h-[42px] px-3 text-[13px] rounded-md border border-[rgb(var(--border-secondary))] bg-[rgb(var(--surface-1))] text-[rgb(var(--text-primary))] placeholder:text-[rgb(var(--text-muted))] outline-none focus:border-gold-2 focus:ring-1 focus:ring-gold-2/20 transition-all";
  const labelClass = "text-[11px] font-semibold uppercase tracking-[0.06em] text-[rgb(var(--text-muted))] mb-1.5 block";

  return (
    <>
      <Dialog
        header="Incident Video"
        maximizable
        visible={visible}
        style={{ width: '480px' }}
        onHide={() => { setVisible(false); resetInputs(); }}
        breakpoints={{ '960px': '75vw', '641px': '95vw' }}
        footer={
          <div className="flex justify-end gap-2 px-6 py-4 border-t border-[rgb(var(--border-primary))]">
            <button style={{background: "var(--gold-grad)"}} onClick={() => handleChildModalOpening()} className="px-4 py-2 rounded-sm hover:bg-gold-3 text-white text-[13px] font-medium transition-colors">Submit</button>
            <button onClick={() => { setVisible(false); resetInputs(); }} className="px-4 py-2 rounded-sm bg-[rgb(var(--surface-2))] border border-[rgb(var(--border-secondary))] text-[rgb(var(--text-primary))] text-[13px] font-medium hover:bg-[rgb(var(--surface-3))] transition-colors">Cancel</button>
          </div>
        }
        pt={{
          header: { className: "!bg-[rgb(var(--surface-1))] !border-b !border-[rgb(var(--border-primary))] !px-6 !py-4" },
          content: { className: "!bg-[rgb(var(--surface-1))] !p-0" },
          footer: { className: "!bg-[rgb(var(--surface-1))] !p-0" },
          headerTitle: { className: "!text-[16px] !font-semibold !text-[rgb(var(--text-primary))]" },
        }}
      >
        <div className="flex flex-col gap-5 p-6">
          {loading ? (
            <div className="flex justify-center items-center h-[200px]">
              <ProgressSpinner style={{ width: '50px', height: '50px' }} strokeWidth="3" animationDuration=".5s" />
            </div>
          ) : (
            <>
              {/* Subject */}
              <div>
                <label className={labelClass}>Subject</label>
                <input className={inputClass} value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Enter subject" />
              </div>

              {/* Store */}
              <div>
                <label className={labelClass}>Store</label>
                <StoresDropDown storename={storename} setStoreName={setStoreName} setStoreValue={setSelectedStore} storevalue={selectedStore} />
              </div>

              {/* Date + Time row */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>Incident Date</label>
                  <div className="[&_.p-calendar]:w-full [&_.p-inputtext]:!h-[42px] [&_.p-inputtext]:!text-[13px] [&_.p-inputtext]:!rounded-l-sm [&_.p-inputtext]:!rounded-r-none [&_.p-inputtext]:!border [&_.p-inputtext]:!border-[rgb(var(--border-secondary))] [&_.p-inputtext]:!bg-[rgb(var(--surface-1))] [&_.p-inputtext]:!text-[rgb(var(--text-primary))] [&_.p-button]:!h-[42px] [&_.p-button]:!w-[42px] [&_.p-button]:!rounded-r-sm [&_.p-button]:!rounded-l-none [&_.p-button]:!bg-gold-2 [&_.p-button]:!border-gold-2">
                    <Calendar value={date} maxDate={new Date()} onChange={(e) => setDate(e.value)} showIcon />
                  </div>
                </div>
                <div>
                  <label className={labelClass}>Incident Time (Approx)</label>
                  <input className={inputClass} value={time} onChange={(e) => setTime(e.target.value)} placeholder="e.g. 2:30 PM" />
                </div>
              </div>

              {/* Details */}
              <div>
                <label className={labelClass}>Incident Details</label>
                <textarea
                  className="w-full min-h-[120px] px-3 py-2.5 text-[13px] rounded-sm border border-[rgb(var(--border-secondary))] bg-[rgb(var(--surface-1))] text-[rgb(var(--text-primary))] placeholder:text-[rgb(var(--text-muted))] outline-none focus:border-gold-2 focus:ring-1 focus:ring-gold-2/20 transition-all resize-y font-[inherit]"
                  value={details}
                  onChange={(e) => setDetails(e.target.value)}
                  placeholder="Describe the incident..."
                />
              </div>
            </>
          )}
        </div>
      </Dialog>

      {/* Confirmation Dialog */}
      <Dialog
        header="Submit Client Query"
        visible={showChildModal}
        style={{ width: '420px' }}
        onHide={() => setShowChildModal(false)}
        breakpoints={{ '960px': '75vw', '641px': '95vw' }}
        footer={
          <div className="flex justify-end gap-2 px-6 py-4 border-t border-[rgb(var(--border-primary))]">
            <button onClick={() => { submitEvidenceDetails(); setShowChildModal(false); }} className="px-4 py-2 rounded-md bg-gold-2 hover:bg-gold-3 text-white text-[13px] font-medium transition-colors">Confirm</button>
            <button onClick={() => setShowChildModal(false)} className="px-4 py-2 rounded-md bg-[rgb(var(--surface-2))] border border-[rgb(var(--border-secondary))] text-[rgb(var(--text-primary))] text-[13px] font-medium hover:bg-[rgb(var(--surface-3))] transition-colors">Cancel</button>
          </div>
        }
        pt={{
          header: { className: "!bg-[rgb(var(--surface-1))] !border-b !border-[rgb(var(--border-primary))] !px-6 !py-4" },
          content: { className: "!bg-[rgb(var(--surface-1))] !p-6" },
          footer: { className: "!bg-[rgb(var(--surface-1))] !p-0" },
          headerTitle: { className: "!text-[15px] !font-semibold !text-[rgb(var(--text-primary))]" },
        }}
      >
        <p className="text-[13px] text-[rgb(var(--text-secondary))] leading-relaxed">
          <span className="font-semibold text-[rgb(var(--text-primary))]">Note:</span> We accept a maximum of 3 queries per month. Each query duration should not exceed 2 hours. Exceeding limits will be chargeable.
        </p>
      </Dialog>
    </>
  );
};

export default IncidentVideoModal;
